// components/product/RelatedProductsBit.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Reveal, StaggerContainer } from '../animations/Reveal';
import LuxuryButton from '../ui/LuxuryButton';
import LuxuryLoading from '../ui/LuxuryLoading';
import {
    FaShoppingBag,
    FaHeart,
    FaEye,
    FaStar,
    FaArrowRight
} from 'react-icons/fa';
import '../../styles/globals.css';
import { CartService } from "../../api/cart.api";

const RelatedProductsBit = ({ relatedProducts = [], currentProductId }) => {
    const navigate = useNavigate();
    const [addingToCart, setAddingToCart] = useState({});

    // Mock related products for demo
    const mockRelatedProducts = [
        {
            id: '6978c76e8b2433ef33ec85da',
            name: 'Hydra-Fix Concentrate',
            category: 'Hydrating Serum',
            description: 'Intense hydration with hyaluronic acid complex',
            price: 165,
            originalPrice: 200,
            discount: 18,
            rating: 4.8,
            image: 'https://encrypted-tbn3.gstatic.com/shopping?q=tbn:ANd9GcTnzJo9mOcFxUbzXCywO3njs1sqEnYecMNUq2k1tDzm3mmHujEnwVsg97NPdJWFBgipM86uFWYSUox8SSBK_TJYEeTpkdLPknWO1hOXPtpcPBl_RGs8nbwv',
            isNew: true,
            isBestseller: false
        },
        {
            id: '6978c76e8b2433ef33ec85db',
            name: 'Night Renewal Elixir',
            category: 'Night Treatment',
            description: 'Overnight repair and rejuvenation',
            price: 195,
            originalPrice: 230,
            discount: 15,
            rating: 4.9,
            image: 'https://encrypted-tbn3.gstatic.com/shopping?q=tbn:ANd9GcRjsbrbyj5-lfWc_ABCrOVewa8v8lLvGOGIOna7lCq6gE5wsX3fZWmLZ55OipK7ACJ0Www4MdbI6lZkO7Snx56r5eTxTF-mrQHmEZoTs3zdmBCu0hvKuAzanA',
            isNew: false,
            isBestseller: true
        },
        {
            id: '6978c76e8b2433ef33ec85dc',
            name: 'Radiance Boosting Cream',
            category: 'Moisturizer',
            description: 'Daily moisturizer with vitamin C',
            price: 145,
            originalPrice: 180,
            discount: 19,
            rating: 4.7,
            image: 'https://encrypted-tbn3.gstatic.com/shopping?q=tbn:ANd9GcTnzJo9mOcFxUbzXCywO3njs1sqEnYecMNUq2k1tDzm3mmHujEnwVsg97NPdJWFBgipM86uFWYSUox8SSBK_TJYEeTpkdLPknWO1hOXPtpcPBl_RGs8nbwv',
            isNew: true,
            isBestseller: false
        },
        {
            id: '6978c76e8b2433ef33ec85dd',
            name: 'Eye Revitalizer',
            category: 'Eye Treatment',
            description: 'Targeted treatment for fine lines',
            price: 125,
            originalPrice: 150,
            discount: 17,
            rating: 4.6,
            image: 'https://encrypted-tbn3.gstatic.com/shopping?q=tbn:ANd9GcRjsbrbyj5-lfWc_ABCrOVewa8v8lLvGOGIOna7lCq6gE5wsX3fZWmLZ55OipK7ACJ0Www4MdbI6lZkO7Snx56r5eTxTF-mrQHmEZoTs3zdmBCu0hvKuAzanA',
            isNew: false,
            isBestseller: true
        }
    ];

    const productsToShow = relatedProducts.length > 0 ? relatedProducts : mockRelatedProducts;

    const handleAddToCart = async (productId) => {
        try {
            setAddingToCart(prev => ({ ...prev, [productId]: true }));
            await CartService.addToCart({
                productId,
                quantity: 1
            });
            // Show success notification
            console.log('Added to cart successfully');
        } catch (error) {
            console.error('Error adding to cart:', error);
            // Show error notification
        } finally {
            setAddingToCart(prev => ({ ...prev, [productId]: false }));
        }
    };

    const handleQuickView = (product) => {
        navigate(`/product/${product.id}`);
    };

    return (
        <div className="space-y-16">
            {/* Header */}
            <div className="text-center space-y-4">
                <Reveal>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-sage/10 rounded-full">
                        <FaArrowRight className="w-3 h-3 text-sage" />
                        <span className="text-sm tracking-widest uppercase text-sage">
                            Complete Your Ritual
                        </span>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <h2 className="text-4xl md:text-5xl font-heading font-light">
                        Discover <span className="italic text-champagne">More</span> from{' '}
                        <span className="italic text-sage">KROMAIC</span>
                    </h2>
                </Reveal>

                <Reveal delay={0.4}>
                    <p className="text-xl text-charcoal/60 max-w-2xl mx-auto">
                        Build your complete skincare ritual with our curated collection
                    </p>
                </Reveal>
            </div>

            {/* Products Grid */}
            <StaggerContainer stagger={0.05}>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {productsToShow.map((product, index) => (
                        <RelatedProductCard
                            key={product.id}
                            product={product}
                            index={index}
                            onAddToCart={() => handleAddToCart(product.id)}
                            onQuickView={() => handleQuickView(product)}
                            isLoading={addingToCart[product.id]}
                            isCurrentProduct={product.id === currentProductId}
                        />
                    ))}
                </div>
            </StaggerContainer>

            {/* View All Button */}
            <Reveal delay={0.6}>
                <div className="text-center pt-12">
                    <LuxuryButton
                        variant="secondary"
                        size="large"
                        icon={FaArrowRight}
                        iconPosition="right"
                        onClick={() => navigate('/shop')}
                    >
                        View Complete Collection
                    </LuxuryButton>
                </div>
            </Reveal>
        </div>
    );
};

const RelatedProductCard = ({ product, index, onAddToCart, onQuickView, isLoading, isCurrentProduct }) => {
    if (isCurrentProduct) return null;

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 40 },
                visible: { opacity: 1, y: 0 }
            }}
            whileHover="hover"
            className="group"
        >
            <div className="relative bg-white rounded-2xl overflow-hidden shadow-subtle hover:shadow-lift transition-all duration-500">
                {/* Image */}
                <div className="relative aspect-square overflow-hidden">
                    <motion.img
                        src={product.image || product.images?.[0]}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                    />

                    {/* Badges */}
                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                        {product.isNew && (
                            <span className="px-3 py-1 bg-sage text-white text-xs rounded-full">
                                New
                            </span>
                        )}
                        {product.isBestseller && (
                            <span className="px-3 py-1 bg-champagne text-charcoal text-xs rounded-full">
                                Bestseller
                            </span>
                        )}
                        {product.discount && (
                            <span className="px-3 py-1 bg-charcoal text-white text-xs rounded-full">
                                -{product.discount}%
                            </span>
                        )}
                    </div>

                    {/* Quick Actions */}
                    <motion.div
                        className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        initial={{ x: 20 }}
                    >
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-medium"
                            onClick={onQuickView}
                        >
                            <FaEye className="w-4 h-4 text-charcoal" />
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-medium"
                        >
                            <FaHeart className="w-4 h-4 text-charcoal" />
                        </motion.button>
                    </motion.div>
                </div>

                {/* Product Info */}
                <div className="p-6">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-xs tracking-widest uppercase text-sage">
                                {product.category}
                            </span>
                            <div className="flex items-center gap-1">
                                <FaStar className="w-3 h-3 text-champagne" />
                                <span className="text-sm text-charcoal/60">{product.rating || 4.5}</span>
                            </div>
                        </div>

                        <h3 className="text-xl font-medium text-charcoal">
                            {product.name}
                        </h3>

                        <p className="text-sm text-charcoal/60 line-clamp-2">
                            {product.description || product.benefit}
                        </p>

                        <div className="flex items-center justify-between pt-4">
                            <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-light text-charcoal">
                                    ${product.price}
                                </span>
                                {product.originalPrice && product.originalPrice > product.price && (
                                    <span className="text-sm text-charcoal/40 line-through">
                                        ${product.originalPrice}
                                    </span>
                                )}
                            </div>

                            <button
                                onClick={onAddToCart}
                                disabled={isLoading}
                                className="px-4 py-2 bg-champagne text-white text-sm rounded-full hover:bg-champagne/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Adding...' : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default RelatedProductsBit;